import type {
  WorkExperienceBullet,
  WorkExperienceCompany,
  WorkExperienceRole,
} from '@applicator/shared'

export function setBulletsIncluded(
  bulletPoints: WorkExperienceBullet[] | undefined,
  included: boolean
): WorkExperienceBullet[] {
  return (bulletPoints || []).map((bullet) => ({ ...bullet, included }))
}

/** Full role subtree: role flag, position/location/type fields, all bullets. */
export function cascadeRoleIncluded(role: WorkExperienceRole, included: boolean): WorkExperienceRole {
  return {
    ...role,
    included,
    positionIncluded: included,
    locationIncluded: included,
    employmentTypeIncluded: included ? (role.employmentTypeIncluded ?? true) : false,
    bulletPoints: setBulletsIncluded(role.bulletPoints, included),
  }
}

/** Company + every role and bullet underneath. */
export function cascadeCompanyIncluded(
  company: WorkExperienceCompany,
  included: boolean
): WorkExperienceCompany {
  return {
    ...company,
    included,
    nameIncluded: included,
    descriptionIncluded: included,
    roles: (company.roles || []).map((role) => cascadeRoleIncluded(role, included)),
  }
}

/** Turn on company header (name + description) when a role is enabled from the UI. */
export function enableCompanyHeader(company: WorkExperienceCompany): WorkExperienceCompany {
  return {
    ...company,
    included: true,
    nameIncluded: true,
    descriptionIncluded: true,
  }
}

/** Turn on company and fully enable one role (all bullets). */
export function enableCompanyAndRole(
  company: WorkExperienceCompany,
  roleId: string
): WorkExperienceCompany {
  const withHeader = enableCompanyHeader(company)
  return {
    ...withHeader,
    roles: (withHeader.roles || []).map((role) =>
      role.id === roleId ? cascadeRoleIncluded(role, true) : role
    ),
  }
}

/** Disable one role subtree; company stays as-is. */
export function disableRoleSubtree(
  company: WorkExperienceCompany,
  roleId: string
): WorkExperienceCompany {
  return {
    ...company,
    roles: (company.roles || []).map((role) =>
      role.id === roleId ? cascadeRoleIncluded(role, false) : role
    ),
  }
}

/** Turn on role header fields shown with the location row (location, type, dates). */
export function enableRoleHeaderFields(role: WorkExperienceRole): WorkExperienceRole {
  return {
    ...role,
    included: true,
    positionIncluded: true,
    locationIncluded: true,
    employmentTypeIncluded: true,
  }
}

/** Bullet turned on: company + role active; only that bullet toggled unless enabling whole role. */
export function applyBulletToggle(
  company: WorkExperienceCompany,
  roleId: string,
  bulletId: string
): WorkExperienceCompany {
  const role = (company.roles || []).find((r) => r.id === roleId)
  if (!role) return company

  const bullet = (role.bulletPoints || []).find((b) => b.id === bulletId)
  if (!bullet) return company

  const nextBulletIncluded = !isBulletEffectivelyChecked(company, role, bullet)

  const updatedRoles = (company.roles || []).map((r) => {
    if (r.id !== roleId) return r
    const nextBullets = (r.bulletPoints || []).map((b) =>
      b.id === bulletId ? { ...b, included: nextBulletIncluded } : b
    )
    if (nextBulletIncluded) {
      return {
        ...enableRoleHeaderFields(r),
        bulletPoints: nextBullets,
      }
    }
    return { ...r, bulletPoints: nextBullets }
  })

  return nextBulletIncluded
    ? {
        ...enableCompanyHeader(company),
        roles: updatedRoles,
      }
    : {
        ...company,
        roles: updatedRoles,
      }
}

/** Checkbox state: company off ⇒ role position reads off even if stored flags differ. */
export function isRolePositionCheckboxChecked(
  company: WorkExperienceCompany,
  role: WorkExperienceRole,
): boolean {
  if (!company.included) return false
  return role.positionIncluded ?? true
}

export function isRoleLocationCheckboxChecked(
  company: WorkExperienceCompany,
  role: WorkExperienceRole,
): boolean {
  if (!company.included) return false
  return role.locationIncluded ?? true
}

/** UI + preview: bullet is on only when company is on and the bullet flag is on. */
export function isBulletCheckboxChecked(
  company: WorkExperienceCompany,
  role: WorkExperienceRole,
  bullet: WorkExperienceBullet,
): boolean {
  if (!company.included) return false
  return bullet.included
}

function isBulletEffectivelyChecked(
  company: WorkExperienceCompany,
  role: WorkExperienceRole,
  bullet: WorkExperienceBullet,
): boolean {
  return isBulletCheckboxChecked(company, role, bullet)
}

/** Repair stored state where company is off but child rows still have included flags. */
export function normalizeWorkExperienceInclusionHierarchy(
  companies: WorkExperienceCompany[],
): WorkExperienceCompany[] {
  return companies.map((company) =>
    company.included ? company : cascadeCompanyIncluded(company, false),
  )
}

export function toggleRolePositionInCompany(
  company: WorkExperienceCompany,
  roleId: string,
): WorkExperienceCompany {
  const role = (company.roles || []).find((r) => r.id === roleId)
  if (!role) return company

  if (role.positionIncluded ?? true) {
    return disableRoleSubtree(company, roleId)
  }
  return enableCompanyAndRole(company, roleId)
}

export function toggleRoleLocationInCompany(
  company: WorkExperienceCompany,
  roleId: string,
): WorkExperienceCompany {
  const role = (company.roles || []).find((r) => r.id === roleId)
  if (!role) return company

  if (role.locationIncluded ?? true) {
    return {
      ...company,
      roles: (company.roles || []).map((r) =>
        r.id === roleId ? { ...r, locationIncluded: false } : r,
      ),
    }
  }

  return {
    ...enableCompanyHeader(company),
    roles: (company.roles || []).map((r) =>
      r.id === roleId
        ? enableRoleHeaderFields({
            ...r,
            positionIncluded: r.positionIncluded ?? true,
          })
        : r,
    ),
  }
}

export function toggleBulletInCompany(
  company: WorkExperienceCompany,
  roleId: string,
  bulletId: string,
): WorkExperienceCompany {
  return applyBulletToggle(company, roleId, bulletId)
}
